import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { StoreContext } from '../contexts/StoreContext';
import { navItems } from '../constants/navItems';
import { db } from '../firebase';
import { MAX_CONTENT_WIDTH, SPACING_BUTTON } from '../constants/layout';
import { COLORS } from '../constants/colors';
import { formatAddress } from '../utils/address';
import './Footer.css';

export default function Footer() {
  const { store } = useContext(StoreContext);
  const [categories, setCategories] = useState([]);
  const businessName = store?.name?.trim();
  const currentYear = new Date().getFullYear();
  const formattedAddress = formatAddress(store?.location);
  const phoneNumber = store?.storeContact?.phoneNumber?.trim();
  const emailAddress = store?.storeContact?.email?.trim();
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';

  useEffect(() => {
    let isMounted = true;

    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, 'categories'));
        if (!isMounted) return;
        const fetched = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cat => !!(cat?.title || cat?.name))
          .sort((a, b) => {
            const titleA = (a.title || a.name || '').toLowerCase();
            const titleB = (b.title || b.name || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });
        setCategories(fetched);
      } catch (err) {
        if (isMounted) {
          setCategories([]);
        }
      }
    }

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const mapsUrl = () => {
    if (!formattedAddress) return '#';
    const addressString = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps/search/?api=1&query=${addressString}`;
  };

  const address = formattedAddress;

  return (
    <footer className="footer" style={{ background: COLORS.lightBackground }}>
      <div
        className="footer-inner"
        style={{
          maxWidth: MAX_CONTENT_WIDTH,
          margin: '0 auto',
          width: '100%',
          padding: `0 ${SPACING_BUTTON}`,
          boxSizing: 'border-box',
        }}
      >
        <div className="footer-columns">
          {/* Brand column */}
          <div className="footer-brand">
            <Link to="/" className="footer-brand-name">{businessName}</Link>
            {address && (
              <a href={mapsUrl()} target="_blank" rel="noopener noreferrer" className="footer-link">
                {address}
              </a>
            )}
            {phoneNumber && (
              <span className="footer-link">
                Call or text us at{' '}
                <a href={phoneHref} className="footer-link footer-link-inline">{phoneNumber}</a>
              </span>
            )}
            {emailAddress && (
              <a href={`mailto:${emailAddress}`} className="footer-link">{emailAddress}</a>
            )}
          </div>

          <div className="footer-links-group">
            {/* Quick Links column */}
            <div className="footer-section">
              <p className="footer-heading">Quick Links</p>
              <div className="footer-links">
                {navItems.map(item => (
                  <Link key={item.path} to={item.path} className="footer-link">
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Categories column */}
            {categories.length > 0 && (
              <div className="footer-section">
                <p className="footer-heading">Categories</p>
                <div className="footer-links">
                  {categories.map(category => {
                    const label = category.title || category.name || 'Category';
                    return (
                      <Link
                        key={category.id}
                        to={`/category/${category.id}`}
                        className="footer-link"
                      >
                        {label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copy">{`\u00A9 ${currentYear} ${businessName}. All rights reserved.`}</p>
        </div>
      </div>
    </footer>
  );
}
